import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import Home from "../../../common-utilities/components/pdf-signature-manager";
import { getCfApprovalV2ById, approveCfForm } from "../cf-v2-create-form/api";
import { invokeUploadFile } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import { CF_APPROVAL_COMMON_ROUTE_V2 } from "../../../cf-approval/utils/constants";
import { validateStepInputs } from "../cf-v2-form-details/cf-v2-step-input-form";
import { getSignAndApproveConfig } from "../../utils/sign-and-approve-config";

const todayDateString = () => new Date().toISOString().slice(0, 10);

const CfV2AffixSignature = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const allStepsMaster = useSelector((state) => state.cfApprovalV2.stepMaster) || [];

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCfApprovalV2ById(id)
      .then((data) => setForm(data))
      .catch(() => toast.error("Failed to load CF form"))
      .finally(() => setLoading(false));
  }, [id]);

  const currentStepNo = form?.currentStepDetails?.stepNumber;
  const stepConfig = getSignAndApproveConfig(currentStepNo);
  const stepMaster = (allStepsMaster || []).find((s) => s.stepNumber === currentStepNo);

  const loiUrl = stepConfig
    ? form?.stepInputs?.[String(stepConfig.sourceStepNumber)]?.[stepConfig.sourceFieldKey]
    : null;

  const handleSignAndSave = async (signedBlob) => {
    try {
      const formData = new FormData();
      formData.append("upload", signedBlob, `${stepConfig.outputFieldKey}.pdf`);
      const uploadRes = await invokeUploadFile(`${CF_APPROVAL_HOSTNAME}${REST_URLS.UPLOAD_FILES}`, formData);
      const url = uploadRes.data[0].split("?")[0];

      const inputs = { [stepConfig.outputFieldKey]: url };
      const hasDateField = (stepMaster?.inputFields || []).some((f) => f.key === stepConfig.outputDateFieldKey);
      if (hasDateField) {
        inputs[stepConfig.outputDateFieldKey] = todayDateString();
      }

      const validationError = validateStepInputs(stepMaster?.inputFields || [], inputs);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      await approveCfForm(id, { comment: "", inputs });
      toast.success("LOI signed and CF form approved successfully");
      navigate(`${CF_APPROVAL_COMMON_ROUTE_V2}/form-details/${id}`);
    } catch (error) {
      toast.error(error?.message || "Failed to save the signed LOI");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={700}>
          {stepConfig?.buttonLabel || "Affix Signature"}
        </Typography>
      </Stack>

      {loading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      )}

      {!loading && !stepConfig && (
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate(-1)}>Go back</Button>
        }>
          This form is not currently on a step that supports signing.
        </Alert>
      )}

      {!loading && stepConfig && (
        <Home
          initialPdfUrl={loiUrl}
          initialPdfLabel="LOI for Signing"
          saveButtonLabel="Save & Send to CF"
          onSave={handleSignAndSave}
        />
      )}
    </Box>
  );
};

export default CfV2AffixSignature;
